import { deleteAllCookies, getCookie } from "./Utils";
import { disconnectSocket } from "./socketClient";

// Staff web sign-in session (Staff Login Sessions plan, phase 2).
// The server decides when a sign-in ends (30 minutes without activity, 12 hours at most); this file keeps what the
// server last said, works out the warnings from it, and carries the "you were signed out" reason to the sign-in page.
// Everything lives in localStorage so every tab of this browser sees the same thing (the token cookie is shared too).

export const IDLE_WARN_MS = 2 * 60 * 1000; // pop-up this long before the idle sign-out
export const CAP_WARN_MS = 10 * 60 * 1000; // pop-up this long before the 12-hour limit
export const PING_EVERY_MS = 60 * 1000; // the server allows 6 activity calls a minute; we send at most 1

const SESSION_KEY = "staffSession";
const REASON_KEY = "staffSignOutReason"; // why the last sign-in ended (for the sign-in page)
const EVENT_KEY = "staffSignOutEvent"; // changing it tells the other tabs to leave
const RETURN_KEY = "staffReturnTo"; // the page to open again after the next sign-in
const PING_KEY = "staffLastActivityPing";

const REASON_MAX_AGE_MS = 60 * 60 * 1000;
const RETURN_MAX_AGE_MS = 12 * 60 * 60 * 1000;

// 401 codes that mean "this sign-in is over". role_inactive / department_inactive are not here: the session stays
// alive and works again when the role or department is switched back on.
export const SESSION_END_CODES = new Set([
    "token_invalid",
    "session_idle",
    "session_expired",
    "session_revoked",
    "session_replaced",
    "password_changed",
    "account_inactive",
]);

const FALLBACK_MESSAGES = {
    token_invalid: "Please sign in again.",
    session_idle: "You were signed out after 30 minutes of inactivity.",
    session_expired: "Your sign-in has ended. Please sign in again.",
    session_revoked: "You were signed out. Please sign in again.",
    session_replaced: "You signed in on another device.",
    password_changed: "Your password was changed. Please sign in again.",
    account_inactive: "Account is not active",
};

const read = (key) => {
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
};
const write = (key, value) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        // private mode / storage full: the page still works, tabs just do not sync
    }
};
const remove = (key) => {
    try {
        window.localStorage.removeItem(key);
    } catch (error) {
        // nothing to do
    }
};

const ms = (value) => {
    const parsed = typeof value === "number" ? value : Date.parse(value);
    return Number.isFinite(parsed) ? parsed : NaN;
};

// ---- the session the server last told us about -------------------------------------------------------------

const listeners = new Set();
let storageWired = false;

const emit = () => {
    const session = getSession();
    listeners.forEach((fn) => {
        try {
            fn(session);
        } catch (error) {
            // one bad listener must not stop the others
        }
    });
};

// view = { platform, signedInAt, expiresAt, idleMinutes, idleExpiresAt, serverTime } from sign-in, GET /staff/session
// or POST /staff/session/activity. skewMs lets the screen use the server's clock for "how long is left".
export const saveSession = (view) => {
    if (!view || typeof view !== "object" || !view.expiresAt) return null;
    const serverNow = ms(view.serverTime);
    // Answers can arrive out of order (a slow GET after a newer activity answer). Within one sign-in the idle end
    // only ever moves forward, so an older answer is ignored.
    const current = getSession();
    if (current && current.signedInAt === (view.signedInAt || null)) {
        const had = ms(current.idleExpiresAt);
        const got = ms(view.idleExpiresAt);
        if (Number.isFinite(had) && Number.isFinite(got) && got < had) return current;
    }
    const stored = {
        platform: view.platform || "web",
        signedInAt: view.signedInAt || null,
        expiresAt: view.expiresAt,
        idleMinutes: view.idleMinutes ?? null,
        idleExpiresAt: view.idleExpiresAt || null,
        skewMs: Number.isFinite(serverNow) ? serverNow - Date.now() : 0,
    };
    write(SESSION_KEY, stored);
    emit();
    return stored;
};

export const getSession = () => read(SESSION_KEY);
export const clearSession = () => {
    remove(SESSION_KEY);
    emit();
};

export const serverNow = (session, at = Date.now()) => at + (Number(session?.skewMs) || 0);

// What the screen needs to know at server time `now`.
//   kind    - what ends the sign-in next: "idle" (nobody using the page) or "cap" (the 12-hour limit; activity cannot
//             move it)
//   endsAt  - that moment (ms), leftMs - time left, warn - inside the warning window, over - already past it
export const phaseOf = (session, now) => {
    const cap = ms(session?.expiresAt);
    if (!Number.isFinite(cap) || !Number.isFinite(now)) return { kind: null, endsAt: null, leftMs: null, capEndsAt: null, warn: false, over: false };
    const idleRaw = ms(session?.idleExpiresAt);
    const idle = Number.isFinite(idleRaw) ? Math.min(idleRaw, cap) : cap;
    const kind = idle >= cap ? "cap" : "idle";
    const leftMs = idle - now;
    return {
        kind,
        endsAt: idle,
        leftMs,
        capEndsAt: cap,
        warn: leftMs > 0 && leftMs <= (kind === "cap" ? CAP_WARN_MS : IDLE_WARN_MS),
        over: leftMs <= 0,
    };
};

export const subscribe = (fn) => {
    listeners.add(fn);
    if (!storageWired && typeof window !== "undefined") {
        storageWired = true;
        // Another tab saved a newer session: show it here too.
        window.addEventListener("storage", (event) => {
            if (event.key === SESSION_KEY) emit();
        });
    }
    return () => listeners.delete(fn);
};

// "3:42 PM", or "Fri 3:42 PM" when it is not today.
export const formatClock = (value, at = Date.now()) => {
    const time = ms(value);
    if (!Number.isFinite(time)) return "";
    const date = new Date(time);
    const hm = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return date.toDateString() === new Date(at).toDateString()
        ? hm
        : `${date.toLocaleDateString([], { weekday: "short" })} ${hm}`;
};

// "1:45"
export const formatLeft = (leftMs) => {
    const total = Math.max(0, Math.ceil((Number(leftMs) || 0) / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// ---- why a sign-in ended, and where to go back to ------------------------------------------------------------

export const messageFor = (code, serverMessage) => {
    const text = typeof serverMessage === "string" && serverMessage.trim() ? serverMessage.trim() : "";
    return text || FALLBACK_MESSAGES[code] || FALLBACK_MESSAGES.token_invalid;
};

export const peekReason = () => {
    const reason = read(REASON_KEY);
    if (!reason?.message || !(Date.now() - Number(reason.at) < REASON_MAX_AGE_MS)) return null;
    return reason;
};
export const clearReason = () => remove(REASON_KEY);

// Only an in-app path: starts with one "/", is not "//host", and is not the sign-in page itself.
export const safePath = (path) => {
    if (typeof path !== "string" || path.length > 500) return null;
    if (!path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return null;
    for (let i = 0; i < path.length; i += 1) if (path.charCodeAt(i) < 32) return null; // no control characters
    return path === "/" ? null : path;
};

export const saveReturnTo = (path = `${window.location.pathname}${window.location.search}${window.location.hash}`) => {
    const safe = safePath(path);
    if (safe) write(RETURN_KEY, { path: safe, at: Date.now() });
};
export const peekReturnTo = () => {
    const saved = read(RETURN_KEY);
    return saved && Date.now() - Number(saved.at) < RETURN_MAX_AGE_MS ? safePath(saved.path) : null;
};
export const clearReturnTo = () => remove(RETURN_KEY);

// ---- leaving ---------------------------------------------------------------------------------------------------

const leave = () => {
    disconnectSocket();
    deleteAllCookies();
    clearSession();
    window.location.replace("/");
};

let ending = false;

// The server said this sign-in is over (a 401 with a code, or the socket refused). Clear it here, tell the other tabs,
// remember the reason for the sign-in page and the page to come back to, and go to the sign-in page.
export const endSession = ({ code, message } = {}) => {
    if (ending) return;
    ending = true;
    write(REASON_KEY, { code: code || "token_invalid", message: messageFor(code, message), at: Date.now() });
    clearReturnTo(); // an older return path must not survive when this page is "/" (nothing to save)
    saveReturnTo();
    write(EVENT_KEY, { kind: "ended", at: Date.now() });
    leave();
};

// Sign out has started: from here a 401 (poll, push-token call) must not also "end" the session and write a reason or
// a return path. Without returnToPage the next sign-in opens the dashboard.
export const beginSignOut = ({ returnToPage = false } = {}) => {
    ending = true;
    if (returnToPage) saveReturnTo();
    else clearReturnTo();
};

// The person chose Sign out (or "Sign in again now"). No reason is shown on the sign-in page.
export const markSignedOut = () => {
    ending = true;
    clearReason();
    write(EVENT_KEY, { kind: "logout", at: Date.now() });
    clearSession();
};

// Another tab ended the sign-in: the cookies are already gone, so this tab only has to leave.
export const onOtherTabSignedOut = (callback) => {
    const handler = (event) => {
        if (event.key !== EVENT_KEY || !event.newValue) return;
        if (ending) return;
        ending = true;
        callback();
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
};

export const leaveToSignIn = leave;

// A new sign-in starts clean.
export const startedSignIn = (view) => {
    ending = false;
    clearReason();
    remove(SESSION_KEY); // nothing left over from an earlier sign-in (the cookie dies with the browser, this does not)
    return saveSession(view);
};

// ---- activity ----------------------------------------------------------------------------------------------------

export const lastPingAt = () => Number(read(PING_KEY)) || 0;
export const setLastPingAt = (value) => write(PING_KEY, value);

// Turns "the person is using the page" into at most one activity call a minute, from every tab together:
//   input -> if the last call (any tab) is a minute old, send now; otherwise send once the minute is up.
// `send` returns true when the server answered (so a failed call is tried again on the next input).
export const createActivityTracker = ({ send, every = PING_EVERY_MS, now = () => Date.now(), getLast = lastPingAt, setLast = setLastPingAt, setTimer = setTimeout, clearTimer = clearTimeout }) => {
    let timer = null;
    let busy = false;

    const run = async () => {
        if (busy) return;
        busy = true;
        const previous = getLast();
        setLast(now()); // claimed first, so the other tabs do not send the same call
        let ok = false;
        try {
            ok = await send();
        } catch (error) {
            ok = false;
        }
        if (!ok) setLast(previous);
        busy = false;
    };

    const onInput = () => {
        const wait = every - (now() - getLast());
        if (wait <= 0) {
            run();
        } else if (!timer) {
            timer = setTimer(() => {
                timer = null;
                onInput(); // another tab may have sent in the meantime: then this waits again
            }, wait + 50);
        }
    };

    return {
        onInput,
        now: () => run(), // send immediately ("Stay signed in")
        dispose: () => {
            if (timer) clearTimer(timer);
            timer = null;
        },
    };
};

export const hasToken = () => Boolean(getCookie("token"));
